import React from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useArticles } from '../data/cmsApi';
import { smartImg, smartSrcSet } from '../lib/imgUrl';

const ArticlesSection = () => {
  const { t, i18n } = useTranslation();
  const { articles } = useArticles(null, i18n.language);

  // Home "Usefull" teaser: show the FIRST article (position 1) of each
  // non-story category in a fixed order (Regulations → Good to Know →
  // Equipment). Order comes straight from the per-category CMS position, so
  // the treasurer's reorder in the admin is reflected here 1:1.
  const HOME_ORDER = ['regulations-safety', 'good-to-know', 'equipment-guide'];
  const items = HOME_ORDER
    .map((cat) => (articles || [])
      .filter((a) => a.category === cat)
      .sort((x, y) => (x.display_order || 0) - (y.display_order || 0))[0])
    .filter(Boolean);

  // Hide entirely if no published items (avoid empty title + ghost block)
  if (articles !== null && items.length === 0) return null;

  return (
    <section className="py-20 bg-white" data-testid="home-articles">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('articles.title')}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('articles.subtitle')}</p>
        </div>

        {articles === null ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((article) => (
              <Link key={article.id} to={`/articles/${article.slug}`} className="block">
                <Card className="overflow-hidden border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group bg-white h-full">
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    {article.image ? (
                      <img loading="lazy" decoding="async"
                        src={smartImg(article.image, 800)}
                        srcSet={smartSrcSet(article.image, [400, 800, 1200]) || undefined}
                        sizes="(max-width: 768px) 100vw, 33vw"
                        width={800}
                        height={500}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300" />
                    )}
                    <Badge className="absolute top-4 left-4 bg-white text-gray-900 capitalize">
                      {article.category}
                    </Badge>
                  </div>
                  <CardContent className="p-6">
                    {/* Iter166 — homepage article cards are now date-less:
                        the section showcases evergreen "how to / good to
                        know" guides where a publish-date stamp adds noise
                        without value. The full date is still visible on
                        article detail pages (for time-bound categories). */}
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-gray-700 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center gap-2 text-gray-900 font-medium group-hover:gap-3 transition-all">
                      {t('articles.readMore')}
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ArticlesSection;
